import React, {FC, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import parseISO from 'date-fns/parseISO';
import {MdAdd, MdCancel, MdEdit, MdOpenInNew} from 'react-icons/md';
import EmptyState from '../../../components/EmptyState';
import PatientCompactBanner from '../../../components/Patient/PatientCompactBanner';
import RowActionMenu from '../../../components/RowActionMenu';
import SidePanel from '../../../components/SidePanel';
import RequestTimeframes from '../../../enums/RequestTimeframes';
import {Glum} from '../../../puddleglum';
import {AppointmentDate, getCurrentTimezone, MysqlDate} from '../../../utils/format';
import {bookingRequestOrderDownloadUrl, canViewUploadedOrder} from '../utils/bookingRequestOrder';
import CancelBookingRequestModal from './CancelBookingRequestModal';
import EhrCharges from './EhrCharges';
import ExistingCharges from './ExistingCharges';
import AddOrEditBookingRequestForm from './forms/AddOrEditBookingRequestForm';
import TreatmentSearch from './TreatmentSearch';
import {Heading, Table, Tag, Tbody, Td, Th, Thead, Tr} from 'components/ui/chakra';
import ProviderBookingController from 'puddleglum/api/providers/ProviderBookingController';

interface Props {
	date: Date;
	disabled?: boolean;
}

const TodaysBookingRequests: FC<Props> = ({date, disabled}) => {
	const [mode, setMode] = useState<'charges' | 'appointment'>('appointment');
	const [recordToEdit, setRecordToEdit] = useState<Glum.Models.BookingRequest>();
	const [bookingToCancel, setBookingToCancel] = useState<Glum.Models.BookingRequest>();
	const selectedDate = MysqlDate(date);

	const {data: bookingRequests, refetch} = useQuery(
		['todays-booking-requests', selectedDate],
		async () => {
			const reply = await ProviderBookingController.index({
				start_date: `${selectedDate} 00:00:00`,
				end_date: `${selectedDate} 23:59:59`,
				scheduled: true,
				status: 'confirmed,completed',
				timezone: getCurrentTimezone(),
			});
			return reply.data;
		},
	);

	const handleClose = () => {
		setRecordToEdit(undefined);
		refetch();
	};

	const openUploadedOrder = (bookingRequest: Glum.Models.BookingRequest) => {
		window.open(
			bookingRequestOrderDownloadUrl(bookingRequest.id),
			'_blank',
			'noopener,noreferrer',
		);
	};

	if (!bookingRequests) return null;

	if (bookingRequests.length === 0) {
		return (
			<EmptyState
				heading='No appointments scheduled'
				description='Nothing is on the schedule for this date. Pick another date above, or use the Check-In Lookup to find a booking request that has not been scheduled yet.'
			/>
		);
	}

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th>Patient</Th>
						<Th>Timeframe</Th>
						<Th>Provider / Location</Th>
						<Th>Actions</Th>
					</Tr>
				</Thead>
				<Tbody>
					{bookingRequests.map((bookingRequest) => (
						<Tr key={bookingRequest.id}>
							<Td>
								{bookingRequest.user!.first_name} {bookingRequest.user!.last_name}
							</Td>
							<Td>
								{bookingRequest.starts_at
									? AppointmentDate(parseISO(bookingRequest.starts_at))
									: RequestTimeframes[
											bookingRequest.requested_timeframe as keyof typeof RequestTimeframes
										]}
							</Td>
							<Td>
								{bookingRequest.provider ? bookingRequest.provider.name : 'N/A'}

								{!!bookingRequest.location && (
									<>
										<br />
										<Tag>
											{bookingRequest.location
												? `${bookingRequest.location.name}`
												: ''}
										</Tag>
									</>
								)}
							</Td>
							<Td>
								{bookingRequest.status !== 'canceled' ? (
									<RowActionMenu
										ariaLabel={`Actions for booking request ${bookingRequest.id}`}
										items={[
											{
												label: 'Manage charges',
												icon: <MdAdd />,
												onClick: () => {
													setMode('charges');
													setRecordToEdit(bookingRequest);
												},
												isDisabled: disabled,
											},
											{
												label: 'Edit appointment',
												icon: <MdEdit />,
												onClick: () => {
													setMode('appointment');
													setRecordToEdit(bookingRequest);
												},
												isDisabled: disabled,
											},
											{
												label: 'Cancel booking',
												icon: <MdCancel />,
												onClick: () => setBookingToCancel(bookingRequest),
												isDisabled: disabled,
												tone: 'danger',
											},
											{
												label: 'View order',
												icon: <MdOpenInNew />,
												onClick: () => openUploadedOrder(bookingRequest),
												hidden: !canViewUploadedOrder(bookingRequest),
											},
										]}
									/>
								) : (
									'N/A'
								)}
							</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
			<SidePanel title='Edit Booking Request' isOpen={!!recordToEdit} onClose={handleClose}>
				{recordToEdit && mode === 'appointment' && (
					<AddOrEditBookingRequestForm
						bookingRequest={recordToEdit}
						onSave={handleClose}
						onClose={handleClose}
					/>
				)}
			</SidePanel>
			<SidePanel
				title='Add Charges'
				isOpen={!!recordToEdit && mode === 'charges'}
				onClose={handleClose}
			>
				{recordToEdit?.user && (
					<Heading flex={1} size='md' color='gray.500'>
						{recordToEdit.user.first_name} {recordToEdit.user.last_name}
					</Heading>
				)}
				{recordToEdit?.user && <PatientCompactBanner patient={recordToEdit.user} />}
				{recordToEdit?.user && (
					<TreatmentSearch user={recordToEdit.user} bookingRequest={recordToEdit} />
				)}
				{recordToEdit?.user && (
					<ExistingCharges user={recordToEdit.user} bookingRequest={recordToEdit} />
				)}
				{recordToEdit?.user && (
					<EhrCharges user={recordToEdit.user} bookingRequest={recordToEdit} />
				)}
			</SidePanel>
			<CancelBookingRequestModal
				bookingRequest={bookingToCancel}
				onCanceled={() => {
					setBookingToCancel(undefined);
					refetch();
				}}
				onClose={() => setBookingToCancel(undefined)}
			/>
		</>
	);
};

export default TodaysBookingRequests;
